import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { getForecastSessionsQueryKey } from 'src/keys';
import { useCreateForecastSession } from 'src/services/api/createForecastSession';
import type { SessionSummary } from 'src/services/api/getForecastSessions';
import { useRenameForecastSession } from 'src/services/api/renameForecastSession';

type UseForecastSessionSwitchParams = {
  activeSession?: SessionSummary;
  sessionId: string | null;
};

export function useForecastSessionSwitch({
  activeSession,
  sessionId,
}: UseForecastSessionSwitchParams) {
  const location = useLocation();
  const sessionScopedPath: '/forecast' | '/forecast/$sku' | '/configuration' =
    location.pathname.startsWith('/configuration')
      ? '/configuration'
      : location.pathname.startsWith('/forecast/')
        ? '/forecast/$sku'
        : '/forecast';
  const navigate = useNavigate({ from: sessionScopedPath });
  const queryClient = useQueryClient();
  const [isSwitching, setIsSwitching] = useState(false);
  const { mutateAsync: createForecastSession, isPending: isCreating } =
    useCreateForecastSession();
  const { mutateAsync: renameForecastSession, isPending: isRenaming } =
    useRenameForecastSession();

  const switchSession = async (nextSessionId: string) => {
    if (nextSessionId === sessionId) {
      return;
    }

    setIsSwitching(true);
    try {
      await navigate({
        search: (previous) => ({
          ...previous,
          sessionId: nextSessionId,
        }),
      });
    } finally {
      setIsSwitching(false);
    }
  };

  const createAndSwitch = async (name: string) => {
    const createdSession = await createForecastSession({ name });

    await queryClient.invalidateQueries({
      queryKey: getForecastSessionsQueryKey(),
    });
    await navigate({
      to: '/configuration',
      search: { sessionId: createdSession.id },
    });

    return createdSession;
  };

  const renameActiveSession = async (name: string) => {
    if (!activeSession) {
      return;
    }

    await renameForecastSession({
      sessionId: activeSession.id,
      name,
    });
    await queryClient.invalidateQueries({
      queryKey: getForecastSessionsQueryKey(),
    });
  };

  return {
    isCreating,
    isRenaming,
    isSwitching,
    switchSession,
    createAndSwitch,
    renameActiveSession,
  };
}
